import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Switch,
  message,
  Space,
  Alert,
  Spin,
  Typography,
  Divider,
  Tooltip,
  Modal
} from 'antd';
import {
  RobotOutlined,
  SaveOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
  EditOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { getAIModelConfig, updateAIModelConfig, testAIConfiguration } from '../services/aiConfigService';

const { Option } = Select;
const { Text } = Typography;

/**
 * AI Model Settings Component
 * Allows admin users to configure AI model settings for clinical decision support
 */
const AIModelSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [promptModalVisible, setPromptModalVisible] = useState(false);
  const [promptModalMode, setPromptModalMode] = useState('view'); // 'view' or 'edit'
  const [tempPrompt, setTempPrompt] = useState('');

  useEffect(() => {
    fetchAIConfig();
  }, []);

  /**
   * Fetch AI model configuration
   */
  const fetchAIConfig = async () => {
    setLoading(true);
    try {
      const config = await getAIModelConfig();
      setInitialValues(config);
      form.setFieldsValue(config);
    } catch (error) {
      message.error('Failed to load AI model configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save AI model configuration
   */
  const handleSave = async (values) => {
    setSaving(true);
    try {
      await updateAIModelConfig(values);
      message.success('AI model configuration saved successfully');
      setInitialValues(values);
      setTestResult(null); // Clear test results after config change
    } catch (error) {
      message.error('Failed to save AI model configuration');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Test AI configuration
   */
  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await testAIConfiguration();

      // Transform the new response format to match the expected format
      const transformedResult = {
        configurationValid: result.status === 'success',
        ...result.data,
        error: result.status !== 'success' ? result.message : null
      };

      setTestResult(transformedResult);

      if (result.status === 'success') {
        message.success('AI configuration test passed successfully');
      } else {
        message.warning('AI configuration test completed with warnings');
      }
    } catch (error) {
      setTestResult({
        configurationValid: false,
        error: error.response?.data?.message || error.message
      });
      message.error('AI configuration test failed');
    } finally {
      setTesting(false);
    }
  };

  /**
   * Render test results
   */
  const renderTestResults = () => {
    if (!testResult) return null;

    const { configurationValid, error, ...details } = testResult;

    return (
      <Alert
        message={configurationValid ? "Configuration Test Passed" : "Configuration Test Failed"}
        description={
          <div>
            {error && <Text type="danger">{error}</Text>}
            {configurationValid && (
              <div style={{ marginTop: 8 }}>
                <Text strong>Configuration Details:</Text>
                <ul style={{ marginTop: 4, marginBottom: 0 }}>
                  <li>Provider: {details.aiProvider}</li>

                  {/* Model Configuration */}
                  {details.mainModel && (
                    <>
                      <li><Text strong>🎯 AI Model:</Text></li>
                      <li style={{ marginLeft: 20 }}>Model: {details.mainModel.model}</li>
                      <li style={{ marginLeft: 20 }}>Max Tokens: {details.mainModel.maxTokens}</li>
                      <li style={{ marginLeft: 20 }}>Temperature: {details.mainModel.temperature}</li>
                    </>
                  )}

                  {/* Legacy support for old response format */}
                  {!details.mainModel && details.modelName && (
                    <li>Model: {details.aiProvider}/{details.modelName}</li>
                  )}

                  <li>API Key: {details.apiKeyConfigured ? '✅ Configured' : '❌ Not configured'}</li>
                  <li>Prompt Template: {details.promptTemplateLoaded ? `✅ Loaded (${details.promptLength?.toLocaleString()} chars)` : '❌ Not loaded'}</li>
                  {details.promptSource && <li>Prompt Source: {details.promptSource}</li>}

                  {details.aiModelTest && (
                    <li>
                      AI Model Connectivity: {details.aiModelTest.success ? '✅ Working' : '❌ Failed'}
                      {details.aiModelTest.error && ` (${details.aiModelTest.error})`}
                      {details.aiModelTest.responseLength && ` - Response: ${details.aiModelTest.responseLength} chars`}
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        }
        type={configurationValid ? "success" : "error"}
        showIcon
        style={{ marginTop: 16 }}
      />
    );
  };

  if (loading) {
    return (
      <Card title={<Space><RobotOutlined />AI Model Settings</Space>}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Loading AI configuration...</div>
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <RobotOutlined />
          <span>AI Model Settings</span>
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ExperimentOutlined />}
            onClick={handleTest}
            loading={testing}
          >
            Test Configuration
          </Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={() => form.submit()}
            loading={saving}
          >
            Save Settings
          </Button>
        </Space>
      }
    >
      <Alert
        message="AI Clinical Decision Support Configuration"
        description="Configure the AI model used for generating CARE360 action plans. These settings control how the system generates personalized clinical recommendations."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={initialValues}
      >
        <Form.Item
          name="modelProvider"
          label={
            <Space>
              <span>Model Provider</span>
              <Tooltip title="Select the AI service provider">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: 'Please select a model provider' }]}
        >
          <Select placeholder="Select AI model provider">
            <Option value="openai">OpenAI</Option>
            <Option value="anthropic">Anthropic (Claude)</Option>
            <Option value="azure-openai">Azure OpenAI</Option>
            <Option value="google">Google (Gemini)</Option>
            <Option value="custom">Custom Provider</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="modelName"
          label={
            <Space>
              <span>Model Name</span>
              <Tooltip title="Specific model version (e.g., gpt-4, claude-3-sonnet)">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: 'Please enter the model name' }]}
        >
          <Input placeholder="e.g., gpt-4, claude-3-sonnet, gpt-4-turbo" />
        </Form.Item>

        <Form.Item
          name="apiKey"
          label={
            <Space>
              <span>API Key</span>
              <Tooltip title="API key for the selected provider (stored securely)">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: 'Please enter the API key' }]}
        >
          <Input.Password placeholder="Enter API key" />
        </Form.Item>

        <Form.Item
          name="apiEndpoint"
          label={
            <Space>
              <span>API Endpoint (Optional)</span>
              <Tooltip title="Custom API endpoint URL (required for Azure OpenAI)">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Input placeholder="https://your-resource.openai.azure.com" />
        </Form.Item>

        <Divider />

        <Form.Item
          name="maxTokens"
          label={
            <Space>
              <span>Max Tokens</span>
              <Tooltip title="Maximum number of tokens in AI response">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: 'Please enter max tokens' }]}
        >
          <InputNumber 
            min={100} 
            max={32000} 
            placeholder="4000"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="temperature"
          label={
            <Space>
              <span>Temperature</span>
              <Tooltip title="Controls randomness (0 = deterministic, 2 = very creative)">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: 'Please enter temperature' }]}
        >
          <InputNumber 
            min={0} 
            max={2} 
            step={0.1} 
            placeholder="0.3"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="isActive"
          label="Active Configuration"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider />

        <Form.Item
          name="masterPrompt"
          label={
            <Space>
              <span>Master Prompt Template</span>
              <Tooltip title="The master prompt template used for AI Action Plan generation. This defines the structure and instructions for the AI.">
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
          }
        >
          <Input type="hidden" />
        </Form.Item>

        {/* Master Prompt UI Controls (separate from Form.Item to avoid conflicts) */}
        <div style={{ marginTop: -16, marginBottom: 24 }}>
          <Space>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                setTempPrompt(form.getFieldValue('masterPrompt') || '');
                setPromptModalMode('view');
                setPromptModalVisible(true);
              }}
            >
              View Prompt
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setTempPrompt(form.getFieldValue('masterPrompt') || '');
                setPromptModalMode('edit');
                setPromptModalVisible(true);
              }}
            >
              Edit Prompt
            </Button>
          </Space>
          <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
            {form.getFieldValue('masterPrompt')
              ? `Current prompt: ${form.getFieldValue('masterPrompt').length.toLocaleString()} characters`
              : 'No custom prompt configured (using default)'
            }
          </div>
        </div>
      </Form>

      {renderTestResults()}

      {/* Master Prompt Modal */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            <span>{promptModalMode === 'edit' ? 'Edit Master Prompt Template' : 'View Master Prompt Template'}</span>
          </Space>
        }
        open={promptModalVisible}
        onCancel={() => setPromptModalVisible(false)}
        width={1000}
        footer={
          promptModalMode === 'edit' ? [
            <Button key="cancel" onClick={() => setPromptModalVisible(false)}>
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              onClick={() => {
                form.setFieldsValue({ masterPrompt: tempPrompt });
                setPromptModalVisible(false);
                message.success('Master prompt updated. Remember to save the configuration.');
              }}
            >
              Apply Changes
            </Button>
          ] : [
            <Button key="close" onClick={() => setPromptModalVisible(false)}>
              Close
            </Button>,
            <Button
              key="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setPromptModalMode('edit')}
            >
              Edit Prompt
            </Button>
          ]
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              promptModalMode === 'edit'
                ? "Edit Master Prompt Template"
                : "Master Prompt Template"
            }
            description={
              promptModalMode === 'edit'
                ? "Modify the master prompt template that defines how the AI generates action plans. Use {patient_data} as a placeholder for patient information."
                : "This is the master prompt template used for AI Action Plan generation. It defines the structure, format, and instructions for the AI."
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        <Input.TextArea
          value={tempPrompt}
          onChange={(e) => setTempPrompt(e.target.value)}
          placeholder={
            promptModalMode === 'edit'
              ? "Enter your master prompt template here..."
              : "No prompt configured"
          }
          rows={20}
          readOnly={promptModalMode === 'view'}
          style={{
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            fontSize: '12px'
          }}
        />

        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
          Character count: {tempPrompt.length.toLocaleString()}
          {tempPrompt.length > 10000 && (
            <span style={{ color: '#ff4d4f', marginLeft: 8 }}>
              ⚠️ Very long prompt may affect performance
            </span>
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default AIModelSettings;
